import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { Chat } from '@ai-sdk/angular';

import type { Id } from '@common/types';

import { AiChatHistoryHttpService } from './ai-chat-history.http.service';
import { fitStoredBatch, fromStoredChatMessages, toStoredChatMessages } from './ai-chat-history.util';
import type { SendpulseUIMessage } from './sendpulse-chat-transport';

export type AiChatHistoryTarget = { siteId: Id; pageId: Id };

/**
 * Keeps a chat in step with the conversation saved for the page it is about: loads it when the chat
 * opens on a page, and appends each finished exchange.
 *
 * A save is attempted at most once. The server has no idempotency, so retrying one that may have
 * landed would duplicate it, and one it refused would be refused again on every later save — a turn
 * missing from the saved history costs less than either.
 */
@Injectable()
export class AiChatHistoryService {
    private readonly http = inject(AiChatHistoryHttpService);

    public readonly loading = signal(false);

    private target: AiChatHistoryTarget | null = null;
    /** How many of the chat's messages the server already has. */
    private saved = 0;
    /** Bumped whenever the chat is pointed at another conversation; anything begun before belongs to the old one. */
    private generation = 0;
    private sendGeneration = -1;

    /** Shows the page's saved conversation, unless the chat already holds it. `null` is a page that cannot have one. */
    public async open(chat: Chat<SendpulseUIMessage>, target: AiChatHistoryTarget | null): Promise<void> {
        if (sameTarget(target, this.target)) {
            return;
        }

        const generation = ++this.generation;

        this.target = target;
        this.saved = 0;

        if (chat.status === 'submitted' || chat.status === 'streaming') {
            await chat.stop();
        }

        chat.messages = [];

        if (!target) {
            return;
        }

        this.loading.set(true);

        try {
            await this.fetchInto(chat, target, generation);
        } finally {
            if (generation === this.generation) {
                this.loading.set(false);
            }
        }
    }

    /** Ties the reply about to start to the conversation it was asked in. */
    public beforeSend(): void {
        this.sendGeneration = this.generation;
    }

    public async saveNew(chat: Chat<SendpulseUIMessage>): Promise<void> {
        const target = this.target;
        const generation = this.generation;

        // A reply cut short by switching pages: its messages are gone from the chat along with the
        // rest of that conversation, and what is in it now belongs to another page.
        if (!target || this.sendGeneration !== generation) {
            return;
        }

        const fresh = chat.messages.slice(this.saved);
        const stored = fitStoredBatch(toStoredChatMessages(fresh));

        this.saved = chat.messages.length;

        if (!stored.length) {
            return;
        }

        try {
            const { truncated } = await firstValueFrom(this.http.appendMessages(target.siteId, target.pageId, stored));

            // The server trimmed the conversation to its budget: show what it kept, which is also what
            // the model should be sent from now on.
            if (truncated && generation === this.generation && chat.status === 'ready') {
                await this.fetchInto(chat, target, generation);
            }
        } catch (error) {
            console.error('AI chat: could not save the conversation', error);
        }
    }

    private async fetchInto(
        chat: Chat<SendpulseUIMessage>,
        target: AiChatHistoryTarget,
        generation: number
    ): Promise<void> {
        try {
            const stored = await firstValueFrom(this.http.getMessages(target.siteId, target.pageId));

            if (generation !== this.generation) {
                return;
            }

            chat.messages = fromStoredChatMessages(stored);
            this.saved = chat.messages.length;
        } catch (error) {
            console.error('AI chat: could not load the saved conversation', error);
        }
    }
}

function sameTarget(a: AiChatHistoryTarget | null, b: AiChatHistoryTarget | null): boolean {
    return (
        a === b ||
        (a != null && b != null && String(a.siteId) === String(b.siteId) && String(a.pageId) === String(b.pageId))
    );
}
